import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, Grid, InputLabel, List,
  ListItem, ListItemButton, ListItemText, MenuItem, Paper, Select, TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import PeopleIcon from "@mui/icons-material/People";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { createFilterOptions } from "@mui/material/Autocomplete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { cleanPatientAction } from "../redux/actions/patient";
import { instance } from "../axios";
import dayjs from "dayjs";
import { fmtDt } from "../utils/date";

const filterOptions = createFilterOptions();

const outcomeColors = {
  apto: "success",
  apto_con_restricciones: "warning",
  no_apto: "error",
  en_espera: "default",
  diferido: "default",
};
const outcomeLabels = {
  apto: "Apto",
  apto_con_restricciones: "Apto c/ restricciones",
  no_apto: "No apto",
  en_espera: "En espera",
  diferido: "Diferido",
};

const StatCard = ({ icon, label, value, color }) => (
  <Paper
    sx={{
      p: 2.5,
      display: "flex",
      alignItems: "center",
      gap: 2,
      borderLeft: `4px solid`,
      borderColor: color || "primary.main",
    }}
  >
    <Box sx={{ color: color || "primary.main", display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight="bold">{value ?? "—"}</Typography>
    </Box>
  </Paper>
);

const ShortcutCard = ({ icon, label, description, onClick }) => (
  <Paper
    onClick={onClick}
    sx={{
      p: 3,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 1.5,
      transition: "box-shadow 0.15s, transform 0.15s",
      "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
    }}
  >
    <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle1" fontWeight={600}>{label}</Typography>
    <Typography variant="caption" color="text.secondary">{description}</Typography>
  </Paper>
);

const DashboardMedecin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Nueva visita modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1); // 1 = select patient, 2 = visit details
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [visitType, setVisitType] = useState("evaluacion");
  const [visitDate, setVisitDate] = useState(dayjs());
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    instance
      .get("/visit/my-recent")
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const searchPatients = useCallback((query) => {
    if (!query || query.length < 2) { setPatientOptions([]); return; }
    setLoadingPatients(true);
    instance.get(`/patient?query=${encodeURIComponent(query)}&limit=20`)
      .then((res) => setPatientOptions(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setPatientOptions([]))
      .finally(() => setLoadingPatients(false));
  }, []);

  const handleOpenModal = () => {
    setModalStep(1);
    setSelectedPatient(null);
    setPatientSearch("");
    setPatientOptions([]);
    setVisitType("evaluacion");
    setVisitDate(dayjs());
    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);

  const handleCreateVisit = async () => {
    if (!selectedPatient) return;
    setCreating(true);
    try {
      const res = await instance.post("/visit/", {
        patientId: selectedPatient.id,
        userId: user.id,
        type: visitType,
        visitDate: visitDate.toISOString(),
      });
      setModalOpen(false);
      navigate(`/visits/${res.data.id}`);
    } catch {
      // keep modal open on error
    } finally {
      setCreating(false);
    }
  };

  const shortcuts = [
    {
      icon: <PeopleIcon sx={{ fontSize: 36 }} />,
      label: "Pacientes",
      description: "Buscar o registrar un paciente",
      onClick: () => navigate("/patients"),
    },
    {
      icon: <AddCircleOutlineIcon sx={{ fontSize: 36 }} />,
      label: "Nueva visita",
      description: "Registrar una nueva consulta",
      onClick: handleOpenModal,
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Greeting */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Bienvenido, {user?.displayName?.split(" ")[0] || "Doctor"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dayjs().format("dddd D [de] MMMM [de] YYYY")}
        </Typography>
      </Box>

      {/* Stats */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<EventNoteIcon sx={{ fontSize: 32 }} />}
                label="Consultas hoy"
                value={data?.countToday}
                color="#401119"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<EventNoteIcon sx={{ fontSize: 32 }} />}
                label="Esta semana"
                value={data?.countWeek}
                color="#6b4226"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<EventNoteIcon sx={{ fontSize: 32 }} />}
                label="Este mes"
                value={data?.countMonth}
                color="#9c6b44"
              />
            </Grid>
          </Grid>

          {/* Shortcuts */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {shortcuts.map((s) => (
              <Grid key={s.label} item xs={12} sm={6} md={4}>
                <ShortcutCard {...s} />
              </Grid>
            ))}
          </Grid>

          {/* Recent visits */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AssignmentIcon sx={{ opacity: 0.7 }} />
              <Typography variant="h6">Últimas consultas</Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {!data?.recent?.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                Sin consultas registradas aún.
              </Typography>
            ) : (
              <List disablePadding>
                {data.recent.map((visit) => (
                  <ListItem key={visit.id} disablePadding divider>
                    <ListItemButton onClick={() => navigate(`/visits/${visit.id}`)} sx={{ py: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" fontWeight={500}>
                              {visit.Patient?.displayName || `Paciente #${visit.patient_id}`}
                            </Typography>
                            {visit.Patient?.identityNumber && (
                              <Typography variant="caption" color="text.secondary">
                                {visit.Patient.identityNumber}
                              </Typography>
                            )}
                            {visit.Evaluation?.outcome && (
                              <Chip
                                size="small"
                                label={outcomeLabels[visit.Evaluation.outcome] || visit.Evaluation.outcome}
                                color={outcomeColors[visit.Evaluation.outcome] || "default"}
                              />
                            )}
                            {visit.type === "evaluacion" && !visit.Evaluation && (
                              <Chip size="small" label="Sin evaluación" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: "flex", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
                            <Typography variant="caption" color="text.secondary">
                              {fmtDt(visit.visitDate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">·</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {visit.type === "evaluacion" ? "Consulta médica ocupacional" : "Consulta general"}
                            </Typography>
                            {visit.Evaluation?.evaluationType && (
                              <>
                                <Typography variant="caption" color="text.secondary">·</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {visit.Evaluation.evaluationType}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </>
      )}

      {/* ── Nueva visita modal ─────────────────────────────────── */}
      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="xs">
        {modalStep === 1 ? (
          <>
            <DialogTitle>Nueva visita — Seleccionar paciente</DialogTitle>
            <DialogContent sx={{ pt: 2, overflow: "visible" }}>
              <Autocomplete
                sx={{ mt: 1 }}
                options={patientOptions}
                getOptionLabel={(p) =>
                  p._create
                    ? p.displayName
                    : p.displayName
                      ? `${p.displayName}${p.identityNumber ? ` — ${p.identityNumber}` : ""}`
                      : ""
                }
                filterOptions={(options, params) => {
                  const filtered = filterOptions(options, params);
                  if (params.inputValue.length >= 2) {
                    filtered.push({ _create: true, inputValue: params.inputValue, displayName: `Crear paciente "${params.inputValue}"` });
                  }
                  return filtered;
                }}
                inputValue={patientSearch}
                value={selectedPatient}
                onInputChange={(_, val) => { setPatientSearch(val); searchPatients(val); }}
                onChange={(_, val) => {
                  if (val?._create) {
                    handleCloseModal();
                    dispatch(cleanPatientAction());
                    navigate("/patients/edit", { state: { displayName: val.inputValue } });
                    return;
                  }
                  setSelectedPatient(val);
                }}
                loading={loadingPatients}
                noOptionsText={patientSearch.length < 2 ? "Escribe al menos 2 caracteres" : "Sin resultados"}
                renderOption={(props, option) => (
                  <li {...props} key={option._create ? "_create" : option.id}>
                    {option._create
                      ? <><PersonAddIcon fontSize="small" sx={{ mr: 1, color: "primary.main", verticalAlign: "middle" }} />{option.displayName}</>
                      : `${option.displayName}${option.identityNumber ? ` — ${option.identityNumber}` : ""}`
                    }
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar paciente"
                    autoFocus
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPatients && <CircularProgress size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Cancelar</Button>
              <Button
                variant="contained"
                disabled={!selectedPatient}
                onClick={() => setModalStep(2)}
              >
                Continuar
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Nueva visita — {selectedPatient?.displayName}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                <InputLabel>Tipo de visita</InputLabel>
                <Select
                  value={visitType}
                  label="Tipo de visita"
                  onChange={(e) => setVisitType(e.target.value)}
                >
                  <MenuItem value="consulta" disabled>Consulta general</MenuItem>
                  <MenuItem value="evaluacion">Consulta médica ocupacional</MenuItem>
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <DatePicker
                  label="Fecha de la visita"
                  value={visitDate}
                  onChange={setVisitDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setModalStep(1)}>Atrás</Button>
              <Button variant="contained" onClick={handleCreateVisit} disabled={creating}>
                {creating ? <CircularProgress size={18} /> : "Crear visita"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DashboardMedecin;
